import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Menu, X } from "lucide-react";
import { Button } from "../ui/button";

const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="landing-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-slate-200/60 shadow-[0_4px_30px_rgba(0,0,0,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#10B981] text-white shadow-[0_8px_20px_rgba(16,185,129,0.35)] group-hover:scale-105 transition-transform">
            <Activity className="w-5 h-5" strokeWidth={2.5} />
          </span>
          <span className="font-outfit text-xl font-bold tracking-tight text-[#0F172A]">
            TherapyNote
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-figtree text-sm font-medium text-[#475569] hover:text-[#059669] transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="header-login-link">
            <Button
              variant="ghost"
              className="rounded-full font-figtree text-sm font-medium text-[#0F172A] hover:bg-slate-100 hover:text-[#0F172A] px-5"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/signup" data-testid="header-trial-btn">
            <Button className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6 py-5 shadow-[0_8px_20px_rgba(16,185,129,0.35)] transition-all duration-300 hover:-translate-y-0.5">
              Start free trial
            </Button>
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200 text-[#0F172A]"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200/60 bg-white/90 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="font-figtree text-base font-medium text-[#0F172A]"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-col gap-3 pt-2">
              <Link to="/login" onClick={() => setOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full rounded-full border-slate-200 text-[#0F172A]"
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/signup" onClick={() => setOpen(false)}>
                <Button className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                  Start free trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
